import { inject, Injectable } from "@angular/core";
import { toObservable } from "@angular/core/rxjs-interop";
import { createEffect, EffectNotification, OnRunEffects } from "@ngrx/effects";
import * as streaming from "@pcp/streaming";
import { LessonHubFacade } from "@pcp/streaming";
import { EMPTY, Observable } from "rxjs";
import { filter, map, switchMap } from "rxjs/operators";
import { isNotNullOrUndefined } from "@pcp/core/util";
import { IDisplaySlide } from "@pcp/core/interfaces";
import * as ClassroomEvents from "../../public/classroom-events.actions";
import { ClassroomFacade } from "../../public/yjs/classroom.facade";

/**
 * Consumes classroom-level SignalR commands (hotspot command handoff).
 *
 * The navigation-preview bridge that used to live here moved to
 * `PreviewScreenEffects.onNavigationPreviewNextSlidesChanged$`
 * (`shared/classroom/preview-screen/feature-shell`), next to the preview's own state.
 *
 * Classroom-owned half of the former slide-sync `ClassroomConsumerEffects`
 * bridge (Plan 06 Part 2); the toolbar-owned half lives in
 * `ToolbarSignalrSyncEffects` (`shared/classroom/toolbar/data-access`).
 *
 * Slide-identity / viewBox / textboardPage / dynamicContent are NOT mirrored
 * into NgRx anymore — display components read them directly from
 * `ClassroomFacade` Signals (typically via `ActiveElementUiStateService`
 * `*LayerSignals` / `effective*ForLayer` / `SlidePreviewFacade.appendDynamicContent$`).
 */
@Injectable()
export class ClassroomSyncConsumerEffects implements OnRunEffects {

    private readonly lessonHub = inject(LessonHubFacade);
    private readonly lessonYjs = inject(ClassroomFacade);

    /**
     * Gate on `lessonYjs.hydrated$` so effects fire even pre-lesson (offline
     * draft Y.Doc) where Y.Doc state matters but no SignalR hub is attached.
     */
    ngrxOnRunEffects(
        resolvedEffects$: Observable<EffectNotification>
    ): Observable<EffectNotification> {
        return this.lessonYjs.hydrated$.pipe(
            switchMap(hydrated => hydrated ? resolvedEffects$ : EMPTY),
        );
    }

    /**
     * Re-emit incoming hotspot activations as the local
     * `ClassroomEvents.hotspotActivated` action, scoped to the
     * currently-displayed slide on each layer.
     *
     * Stage D.5 (write-path refactor): the source is the Y.Doc layer-slide
     * Signal (via `toObservable`) instead of the show*SlideInPreviewScreen
     * action bus. switchMap cancels the previous Command-listener whenever
     * the layer's slide changes, so we only forward hotspot commands
     * targeting the CURRENT slide on that layer.
     */
    private readonly listenOnHotspotActivations = (layer: 1 | 2 | 3) =>
        createEffect(() =>
            toObservable(this.lessonYjs.slideForLayer(layer)).pipe(
                filter(isNotNullOrUndefined),
                switchMap((slide) =>
                    this.lessonHub.Command.pipe(
                        filter(streaming.isActivateHotspotCommand),
                        filter((command) => command.forSlideId === slide.id),
                        map((command) =>
                            ClassroomEvents.hotspotActivated({
                                hotspot: command.hotspot,
                                // Slide carried in the bus action is IDisplaySlide;
                                // the Y.Doc Signal returns ITransferDisplaySlide.
                                // Downstream consumers read only the structural-
                                // compatible subset, so the cast is safe.
                                slide: slide as unknown as IDisplaySlide,
                            })
                        )
                    )
                )
            )
        );

    readonly activateHotspotInPrimarySlide$ = this.listenOnHotspotActivations(1);
    readonly activateHotspotInSecondarySlide$ = this.listenOnHotspotActivations(2);
    readonly activateHotspotInTertiarySlide$ = this.listenOnHotspotActivations(3);
}
